import { useState, useMemo } from 'react'
import { useEffect } from 'react'
import {
  Youtube, RefreshCw, Trash2, Pencil, Upload, Eye, ThumbsUp,
  MessageSquare, ExternalLink, Users, Film, Plus, Send,
  BarChart3, Clock, DollarSign, Share2, UserPlus, UserMinus,
  Heart, TrendingUp, Settings, Save, Languages, Download,
  Loader2, FileVideo, Link, X, Check,
} from 'lucide-react'
import { useAuth } from '@/api/hooks/useAuth'
import { useAdminClients } from '@/api/hooks/useAdmin'
import {
  useYouTubeChannels, useSyncYouTubeChannel, useDisconnectYouTubeChannel, useSaveYouTubeChannelTags,
  useYouTubeVideos, useSyncYouTubeVideos,
  useYouTubeComments, useSyncYouTubeComments, useReplyToYouTubeComment,
  useYouTubeAnalytics,
  useYouTubeDefaultDescription, useUpdateYouTubeDefaultDescription,
  type YouTubeChannel, type YouTubeVideo, type YouTubeComment,
  type AnalyticsPeriod,
} from '@/api/hooks/useYouTube'
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from 'recharts'
import {
  useCreateDubbing, useDubbingList, useDeleteDubbing,
  downloadDubbedVideo, useElevenLabsKey, useSaveElevenLabsKey,
  useDeleteElevenLabsKey, type DubbingJob,
} from '@/api/hooks/useElevenLabs'
import { useMediaFolders } from '@/api/hooks/useMediaLibrary'
import type { MediaImage } from '@/types'
import { UploadDialog } from './UploadDialog'
import { EditVideoDialog } from './EditVideoDialog'
import { TagPresetEditor } from './TagPresetEditor'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { toast } from 'sonner'
import { useSearchParams } from 'react-router-dom'

const fmtNum = new Intl.NumberFormat('pt-BR')

function formatDuration(iso: string | null): string {
  if (!iso) return '--'
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)
  if (!match) return iso
  const h = match[1] ? `${match[1]}:` : ''
  const m = (match[2] ?? '0').padStart(2, '0')
  const s = (match[3] ?? '0').padStart(2, '0')
  return `${h}${m}:${s}`
}

function formatDate(ts: number | null): string {
  if (!ts) return '--'
  return new Date(ts).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const privacyLabel: Record<string, string> = {
  public: 'Publico',
  unlisted: 'Nao Listado',
  private: 'Privado',
}

const privacyColor: Record<string, string> = {
  public: 'bg-green-500/10 text-green-500',
  unlisted: 'bg-yellow-500/10 text-yellow-500',
  private: 'bg-red-500/10 text-red-500',
}

export function YouTube() {
  const [searchParams] = useSearchParams()
  const { data: user } = useAuth()
  const isAdmin = user?.isAdmin ?? false

  // Admin: client selector
  const { data: clients } = useAdminClients()
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  // Channel state
  const { data: channels, isLoading: channelsLoading } = useYouTubeChannels()
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null)
  const syncChannel = useSyncYouTubeChannel()
  const disconnectChannel = useDisconnectYouTubeChannel()
  const saveChannelTags = useSaveYouTubeChannelTags()
  const [editingTagsChannelId, setEditingTagsChannelId] = useState<string | null>(null)
  const [tagInput, setTagInput] = useState('')

  // Show success toast from OAuth redirect
  useState(() => {
    const success = searchParams.get('success')
    const channel = searchParams.get('channel')
    if (success === 'youtube_connected' && channel) {
      toast.success(`Canal conectado: ${channel}`)
    }
    const error = searchParams.get('error')
    if (error) {
      const msg = searchParams.get('message') || error
      toast.error(`Erro: ${msg}`)
    }
  })

  // Filter channels by selected user (admin)
  const filteredChannels = useMemo(() => {
    if (!channels) return []
    if (!isAdmin || !selectedUserId) return channels
    return channels.filter((ch) => ch.userId === selectedUserId)
  }, [channels, isAdmin, selectedUserId])

  // Auto-select first channel
  const activeChannel = useMemo(() => {
    if (selectedChannelId) {
      return filteredChannels.find((ch) => ch.id === selectedChannelId) ?? null
    }
    return filteredChannels[0] ?? null
  }, [filteredChannels, selectedChannelId])

  const activeChannelId = activeChannel?.id ?? null

  // Videos state
  const { data: videos, isLoading: videosLoading } = useYouTubeVideos(activeChannelId)
  const syncVideos = useSyncYouTubeVideos()
  const [uploadOpen, setUploadOpen] = useState(false)
  const [editVideo, setEditVideo] = useState<YouTubeVideo | null>(null)
  const [tagPresetsOpen, setTagPresetsOpen] = useState(false)

  // Comments state
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null)
  const { data: comments, isLoading: commentsLoading } = useYouTubeComments(activeChannelId, selectedVideoId)
  const syncComments = useSyncYouTubeComments()
  const replyMut = useReplyToYouTubeComment()
  const [replyText, setReplyText] = useState('')
  const [replyingTo, setReplyingTo] = useState<string | null>(null)

  // Analytics state
  const [analyticsPeriod, setAnalyticsPeriod] = useState<AnalyticsPeriod>('7d')
  const { data: analytics, isLoading: analyticsLoading } = useYouTubeAnalytics(activeChannelId, analyticsPeriod)

  // Default description state
  const { data: defaultDesc, isLoading: defaultDescLoading } = useYouTubeDefaultDescription(activeChannelId)
  const updateDefaultDesc = useUpdateYouTubeDefaultDescription()
  const [descTemplate, setDescTemplate] = useState('')
  const [tagsTemplate, setTagsTemplate] = useState('')

  // Dubbing state
  const { data: dubbingJobs, isLoading: dubbingLoading } = useDubbingList()
  const createDubbing = useCreateDubbing()
  const deleteDubbing = useDeleteDubbing()
  const [dubbingUrl, setDubbingUrl] = useState('')
  const [dubbingFile, setDubbingFile] = useState<File | null>(null)
  const [dubbingTargetLang, setDubbingTargetLang] = useState('en')
  const [dubbingSourceLang, setDubbingSourceLang] = useState('auto')
  const [dubbingName, setDubbingName] = useState('')
  const [dubbingSource, setDubbingSource] = useState<'yt' | 'r2' | null>(null)
  const [selectedGridVideoId, setSelectedGridVideoId] = useState<string | null>(null)

  // R2 videos from media library
  const { data: mediaFolders } = useMediaFolders()
  const r2Videos = useMemo(() => {
    if (!mediaFolders) return []
    return mediaFolders.flatMap(f => f.images.filter((img: MediaImage) => img.mediaType === 'video'))
  }, [mediaFolders])

  // ElevenLabs API Key state
  const { data: elevenLabsKey } = useElevenLabsKey()
  const saveElevenLabsKey = useSaveElevenLabsKey()
  const deleteElevenLabsKey = useDeleteElevenLabsKey()
  const [elevenLabsKeyInput, setElevenLabsKeyInput] = useState('')
  const [showKeyInput, setShowKeyInput] = useState(false)

  useEffect(() => {
    if (defaultDesc) {
      setDescTemplate(defaultDesc.defaultDescription)
      setTagsTemplate(defaultDesc.defaultTags)
    }
  }, [defaultDesc])

  const handleCreateDubbing = () => {
    if (!dubbingUrl && !dubbingFile) {
      toast.error('Cole uma URL ou selecione um arquivo')
      return
    }
    const name = dubbingName || dubbingFile?.name || dubbingUrl.split('/').pop()?.split('?')[0] || 'Dublagem'
    createDubbing.mutate(
      {
        sourceUrl: dubbingFile ? undefined : dubbingUrl,
        file: dubbingFile ?? undefined,
        targetLang: dubbingTargetLang,
        sourceLang: dubbingSourceLang,
        name,
      },
      {
        onSuccess: (data) => {
          toast.success(`Dublagem criada! Tempo estimado: ~${Math.ceil((data.expected_duration_sec || 60) / 60)} min`)
          setDubbingUrl('')
          setDubbingFile(null)
          setDubbingName('')
          setSelectedGridVideoId(null)
        },
        onError: (err) => toast.error(err.message),
      },
    )
  }

  const handleDeleteDubbing = (job: DubbingJob) => {
    if (!confirm(`Excluir dublagem "${job.name || job.dubbing_id}"?`)) return
    deleteDubbing.mutate(job.dubbing_id, {
      onSuccess: () => toast.success('Dublagem excluida'),
      onError: (err) => toast.error(err.message),
    })
  }

  const handleConnect = () => {
    const userId = isAdmin && selectedUserId ? selectedUserId : user?.id
    window.location.href = `/oauth/youtube/redirect?userId=${userId}`
  }

  const handleDisconnect = (ch: YouTubeChannel) => {
    if (!confirm(`Desconectar o canal "${ch.channelTitle}"?`)) return
    disconnectChannel.mutate(ch.id, {
      onSuccess: () => toast.success('Canal desconectado'),
      onError: (err) => toast.error(err.message),
    })
  }

  const handleSyncVideos = () => {
    if (!activeChannelId) return
    syncVideos.mutate(activeChannelId, {
      onSuccess: (data) => toast.success(`${data.synced} videos sincronizados`),
      onError: (err) => toast.error(err.message),
    })
  }

  const handleSyncComments = () => {
    if (!activeChannelId || !selectedVideoId) return
    syncComments.mutate(
      { channelId: activeChannelId, videoId: selectedVideoId },
      {
        onSuccess: (data) => toast.success(`${data.synced} comentarios sincronizados`),
        onError: (err) => toast.error(err.message),
      },
    )
  }

  const handleReply = (commentId: string) => {
    if (!activeChannelId || !selectedVideoId || !replyText.trim()) return
    replyMut.mutate(
      { channelId: activeChannelId, videoId: selectedVideoId, commentId, text: replyText.trim() },
      {
        onSuccess: () => {
          toast.success('Resposta enviada')
          setReplyText('')
          setReplyingTo(null)
        },
        onError: (err) => toast.error(err.message),
      },
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Youtube className="h-6 w-6 text-red-500" />
          <h1 className="text-xl font-bold tracking-tight">YouTube</h1>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && clients && clients.length > 0 && (
            <Select value={selectedUserId ?? 'all'} onValueChange={(v) => setSelectedUserId(v === 'all' ? null : v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Todos os clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button onClick={handleConnect}>
            <Plus className="mr-2 h-4 w-4" />
            Conectar Canal
          </Button>
        </div>
      </div>

      {/* Channel Cards */}
      {channelsLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : filteredChannels.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 p-8 text-center text-muted-foreground">
          <Youtube className="h-12 w-12 opacity-30" />
          <p>Nenhum canal conectado</p>
          <Button variant="outline" onClick={handleConnect}>Conectar Canal</Button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredChannels.map((ch) => (
            <Card
              key={ch.id}
              className={`cursor-pointer p-4 transition-all hover:ring-2 hover:ring-primary/50 ${activeChannelId === ch.id ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setSelectedChannelId(ch.id)}
            >
              <div className="flex items-start gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={ch.channelThumbnail ?? undefined} />
                  <AvatarFallback>{ch.channelTitle.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-semibold">{ch.channelTitle}</h3>
                    {isAdmin && ch.userName && (
                      <Badge variant="outline" className="text-[10px] shrink-0">{ch.userName}</Badge>
                    )}
                  </div>
                  <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{fmtNum.format(ch.subscriberCount)}</span>
                    <span className="flex items-center gap-1"><Film className="h-3 w-3" />{fmtNum.format(ch.videoCount)}</span>
                    <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{fmtNum.format(ch.viewCount)}</span>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={(e) => { e.stopPropagation(); syncChannel.mutate(ch.id, { onSuccess: () => toast.success('Stats atualizados') }) }}
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${syncChannel.isPending ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive"
                    onClick={(e) => { e.stopPropagation(); handleDisconnect(ch) }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              {/* Tags */}
              <div className="mt-2.5 flex flex-wrap items-center gap-1" onClick={(e) => e.stopPropagation()}>
                {(ch.tags || []).map(tag => (
                  <Badge key={tag} variant="secondary" className="text-[9px] rounded-md gap-0.5">
                    {tag}
                    <button
                      className="ml-0.5 hover:text-destructive"
                      onClick={() => saveChannelTags.mutate({ channelId: ch.id, tags: ch.tags.filter(t => t !== tag) })}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
                {editingTagsChannelId === ch.id ? (
                  <form
                    className="flex items-center gap-1"
                    onSubmit={(e) => {
                      e.preventDefault()
                      const val = tagInput.trim().toLowerCase()
                      if (!val) return
                      const current = ch.tags || []
                      if (current.includes(val)) { setTagInput(''); return }
                      saveChannelTags.mutate(
                        { channelId: ch.id, tags: [...current, val] },
                        { onSuccess: () => { setTagInput(''); setEditingTagsChannelId(null) } },
                      )
                    }}
                  >
                    <Input
                      autoFocus
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="tag..."
                      className="h-5 w-20 text-[10px] px-1.5 rounded-md"
                      onBlur={() => { if (!tagInput.trim()) setEditingTagsChannelId(null) }}
                    />
                  </form>
                ) : (
                  <button
                    className="flex items-center gap-0.5 rounded-md border border-dashed border-border/60 px-1.5 py-0.5 text-[9px] text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
                    onClick={() => { setEditingTagsChannelId(ch.id); setTagInput('') }}
                  >
                    <Plus className="h-2.5 w-2.5" /> tag
                  </button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Content Tabs */}
      {activeChannelId && (
        <Tabs defaultValue="videos" className="space-y-4">
          <TabsList>
            <TabsTrigger value="videos">Videos</TabsTrigger>
            <TabsTrigger value="comments">Comentarios</TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart3 className="mr-1.5 h-3.5 w-3.5" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="mr-1.5 h-3.5 w-3.5" />
              Config
            </TabsTrigger>
          </TabsList>

          {/* Videos Tab */}
          <TabsContent value="videos" className="space-y-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleSyncVideos} disabled={syncVideos.isPending}>
                <RefreshCw className={`mr-2 h-4 w-4 ${syncVideos.isPending ? 'animate-spin' : ''}`} />
                Sincronizar
              </Button>
              <Button size="sm" onClick={() => setUploadOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </Button>
              <Button variant="outline" size="sm" onClick={() => setTagPresetsOpen(true)}>
                <Settings className="mr-2 h-4 w-4" />
                Tag Presets
              </Button>
            </div>

            {videosLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
              </div>
            ) : !videos || videos.length === 0 ? (
              <Card className="p-6 text-center text-muted-foreground">
                <p>Nenhum video encontrado. Clique em "Sincronizar" para buscar do YouTube.</p>
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">Thumb</TableHead>
                      <TableHead>Titulo</TableHead>
                      <TableHead className="w-[80px] text-center">Status</TableHead>
                      <TableHead className="w-[90px] text-right">Views</TableHead>
                      <TableHead className="w-[70px] text-right">Likes</TableHead>
                      <TableHead className="w-[70px] text-right">Coment.</TableHead>
                      <TableHead className="w-[70px] text-center">Duracao</TableHead>
                      <TableHead className="w-[90px]">Data</TableHead>
                      <TableHead className="w-[80px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {videos.map((v) => (
                      <TableRow key={v.id}>
                        <TableCell>
                          {v.thumbnailUrl ? (
                            <img src={v.thumbnailUrl} alt="" className="h-10 w-16 rounded object-cover" />
                          ) : (
                            <div className="h-10 w-16 rounded bg-secondary flex items-center justify-center">
                              <Film className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[300px]">
                            <p className="truncate font-medium text-sm">{v.title}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={`text-[10px] ${privacyColor[v.privacyStatus] ?? ''}`}>
                            {privacyLabel[v.privacyStatus] ?? v.privacyStatus}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{fmtNum.format(v.viewCount)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{fmtNum.format(v.likeCount)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{fmtNum.format(v.commentCount)}</TableCell>
                        <TableCell className="text-center text-sm tabular-nums">{formatDuration(v.duration)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDate(v.publishedAt)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => setEditVideo(v)}
                              title="Editar"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => {
                                setSelectedVideoId(v.id)
                                const tabEl = document.querySelector('[data-value="comments"]') as HTMLElement
                                tabEl?.click()
                              }}
                              title="Ver comentarios"
                            >
                              <MessageSquare className="h-3.5 w-3.5" />
                            </Button>
                            <a
                              href={`https://www.youtube.com/watch?v=${v.videoId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent"
                              title="Abrir no YouTube"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4">
            {/* Period selector */}
            <div className="flex items-center gap-2">
              {(['48h', '7d', '28d', '90d', '365d'] as AnalyticsPeriod[]).map((p) => (
                <Button
                  key={p}
                  size="sm"
                  variant={analyticsPeriod === p ? 'default' : 'outline'}
                  onClick={() => setAnalyticsPeriod(p)}
                >
                  {p === '48h' ? '2 dias' : p === '7d' ? '7 dias' : p === '28d' ? '28 dias' : p === '90d' ? '90 dias' : '365 dias'}
                </Button>
              ))}
            </div>

            {analyticsLoading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
              </div>
            ) : !analytics ? (
              <Card className="p-6 text-center text-muted-foreground">
                <p>Nao foi possivel carregar os dados de analytics.</p>
              </Card>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Card className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Eye className="h-4 w-4" />
                      <span>Views</span>
                    </div>
                    <p className="mt-2 text-2xl font-bold tabular-nums">{fmtNum.format(analytics.totals.views)}</p>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Clock className="h-4 w-4" />
                      <span>Tempo de exibicao</span>
                    </div>
                    <p className="mt-2 text-2xl font-bold tabular-nums">
                      {fmtNum.format(Math.round(analytics.totals.estimatedMinutesWatched / 60))}
                      <span className="text-sm font-normal text-muted-foreground ml-1">horas</span>
                    </p>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <TrendingUp className="h-4 w-4" />
                      <span>Duracao media</span>
                    </div>
                    <p className="mt-2 text-2xl font-bold tabular-nums">
                      {Math.floor(analytics.totals.averageViewDuration / 60)}:{String(Math.round(analytics.totals.averageViewDuration % 60)).padStart(2, '0')}
                    </p>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <DollarSign className="h-4 w-4" />
                      <span>Receita estimada</span>
                    </div>
                    {analytics.totals.revenue !== null ? (
                      <p className="mt-2 text-2xl font-bold tabular-nums text-green-500">
                        ${analytics.totals.revenue.toFixed(2)}
                      </p>
                    ) : (
                      <div className="mt-2">
                        <Badge variant="outline" className="text-xs">Nao monetizado</Badge>
                      </div>
                    )}
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Heart className="h-4 w-4" />
                      <span>Likes</span>
                    </div>
                    <p className="mt-2 text-2xl font-bold tabular-nums">{fmtNum.format(analytics.totals.likes)}</p>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <MessageSquare className="h-4 w-4" />
                      <span>Comentarios</span>
                    </div>
                    <p className="mt-2 text-2xl font-bold tabular-nums">{fmtNum.format(analytics.totals.comments)}</p>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Share2 className="h-4 w-4" />
                      <span>Compartilhamentos</span>
                    </div>
                    <p className="mt-2 text-2xl font-bold tabular-nums">{fmtNum.format(analytics.totals.shares)}</p>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <UserPlus className="h-4 w-4 text-green-500" />
                      <span>Inscritos</span>
                    </div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-2xl font-bold tabular-nums text-green-500">+{fmtNum.format(analytics.totals.subscribersGained)}</span>
                      <span className="text-sm tabular-nums text-red-500 flex items-center gap-0.5">
                        <UserMinus className="h-3 w-3" />-{fmtNum.format(analytics.totals.subscribersLost)}
                      </span>
                    </div>
                  </Card>
                </div>

                {/* Chart */}
                {analytics.dailyData.length > 0 && (
                  <Card className="p-4">
                    <h3 className="mb-4 font-semibold text-sm">Views e Tempo de exibicao por dia</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={analytics.dailyData.map((d) => ({
                        day: d.day,
                        Views: d.views,
                        'Horas assistidas': Math.round((d.estimatedMinutesWatched / 60) * 10) / 10,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis
                          dataKey="day"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v: string) => {
                            const parts = v.split('-')
                            return `${parts[2]}/${parts[1]}`
                          }}
                          className="text-muted-foreground"
                        />
                        <YAxis yAxisId="views" tick={{ fontSize: 11 }} className="text-muted-foreground" />
                        <YAxis yAxisId="hours" orientation="right" tick={{ fontSize: 11 }} className="text-muted-foreground" />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
                          labelFormatter={(v: string) => {
                            const parts = v.split('-')
                            return `${parts[2]}/${parts[1]}/${parts[0]}`
                          }}
                        />
                        <Legend />
                        <Line yAxisId="views" type="monotone" dataKey="Views" stroke="var(--primary)" strokeWidth={2} dot={false} />
                        <Line yAxisId="hours" type="monotone" dataKey="Horas assistidas" stroke="#f97316" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Comments Tab */}
          <TabsContent value="comments" className="space-y-4">
            <div className="flex items-center gap-2">
              {videos && videos.length > 0 && (
                <Select
                  value={selectedVideoId ?? ''}
                  onValueChange={(v) => setSelectedVideoId(v || null)}
                >
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder="Selecione um video" />
                  </SelectTrigger>
                  <SelectContent>
                    {videos.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {selectedVideoId && (
                <Button variant="outline" size="sm" onClick={handleSyncComments} disabled={syncComments.isPending}>
                  <RefreshCw className={`mr-2 h-4 w-4 ${syncComments.isPending ? 'animate-spin' : ''}`} />
                  Sincronizar
                </Button>
              )}
            </div>

            {!selectedVideoId ? (
              <Card className="p-6 text-center text-muted-foreground">
                <p>Selecione um video para ver os comentarios</p>
              </Card>
            ) : commentsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
              </div>
            ) : !comments || comments.length === 0 ? (
              <Card className="p-6 text-center text-muted-foreground">
                <p>Nenhum comentario encontrado. Clique em "Sincronizar" para buscar.</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {comments
                  .filter((c) => !c.parentId)
                  .map((c) => {
                    const replies = comments.filter((r) => r.parentId === c.commentId)
                    return (
                      <Card key={c.id} className="p-4">
                        <div className="flex gap-3">
                          <Avatar className="h-8 w-8 shrink-0">
                            <AvatarImage src={c.authorProfileImage ?? undefined} />
                            <AvatarFallback>{c.authorName.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{c.authorName}</span>
                              <span className="text-xs text-muted-foreground">{formatDate(c.publishedAt)}</span>
                              {c.isReplied && <Badge variant="outline" className="text-[9px] bg-green-500/10 text-green-500">Respondido</Badge>}
                            </div>
                            <p className="mt-1 text-sm whitespace-pre-wrap">{c.text}</p>
                            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3" />{c.likeCount}</span>
                              {!c.isReplied && (
                                <button
                                  className="flex items-center gap-1 hover:text-primary transition-colors"
                                  onClick={() => setReplyingTo(replyingTo === c.commentId ? null : c.commentId)}
                                >
                                  <MessageSquare className="h-3 w-3" />Responder
                                </button>
                              )}
                            </div>

                            {/* Reply input */}
                            {replyingTo === c.commentId && (
                              <div className="mt-3 flex gap-2">
                                <Input
                                  value={replyText}
                                  onChange={(e) => setReplyText(e.target.value)}
                                  placeholder="Escreva sua resposta..."
                                  className="flex-1"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault()
                                      handleReply(c.commentId)
                                    }
                                  }}
                                />
                                <Button size="sm" onClick={() => handleReply(c.commentId)} disabled={replyMut.isPending || !replyText.trim()}>
                                  <Send className="h-4 w-4" />
                                </Button>
                              </div>
                            )}

                            {/* Replies */}
                            {replies.length > 0 && (
                              <div className="mt-3 space-y-2 border-l-2 border-border pl-4">
                                {replies.map((r) => (
                                  <div key={r.id} className="flex gap-2">
                                    <Avatar className="h-6 w-6 shrink-0">
                                      <AvatarImage src={r.authorProfileImage ?? undefined} />
                                      <AvatarFallback>{r.authorName.charAt(0)}</AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-xs">{r.authorName}</span>
                                        <span className="text-[10px] text-muted-foreground">{formatDate(r.publishedAt)}</span>
                                      </div>
                                      <p className="text-xs mt-0.5 whitespace-pre-wrap">{r.text}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    )
                  })}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            {defaultDescLoading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
              </div>
            ) : (
              <Card className="p-6 space-y-5">
                <div>
                  <h3 className="font-semibold mb-1">Descricao padrao dos videos</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Essa descricao sera aplicada automaticamente em todo upload que nao tiver descricao personalizada.
                    Use as variaveis: <code className="bg-secondary px-1 py-0.5 rounded text-[11px]">{'{titulo}'}</code> <code className="bg-secondary px-1 py-0.5 rounded text-[11px]">{'{canal}'}</code> <code className="bg-secondary px-1 py-0.5 rounded text-[11px]">{'{data}'}</code>
                  </p>
                  <Textarea
                    rows={10}
                    value={descTemplate}
                    onChange={(e) => setDescTemplate(e.target.value)}
                    placeholder={`Exemplo:\n\n{titulo}\n\n🔥 Acesse nosso site: https://seusite.com\n📱 Instagram: @seuperfil\n💰 Link de vendas: https://seulink.com\n\n#shorts #vendas #marketing`}
                    className="font-mono text-sm"
                  />
                </div>

                <div>
                  <Label>Tags padrao (separadas por virgula)</Label>
                  <Input
                    className="mt-1.5"
                    value={tagsTemplate}
                    onChange={(e) => setTagsTemplate(e.target.value)}
                    placeholder="vendas, marketing, digital, empreendedorismo"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Aplicadas automaticamente quando o upload nao tiver tags personalizadas.
                  </p>
                </div>

                <Button
                  onClick={() => {
                    if (!activeChannelId) return
                    updateDefaultDesc.mutate(
                      { channelId: activeChannelId, defaultDescription: descTemplate, defaultTags: tagsTemplate },
                      {
                        onSuccess: () => toast.success('Configuracoes salvas'),
                        onError: (err) => toast.error(err.message),
                      },
                    )
                  }}
                  disabled={updateDefaultDesc.isPending}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {updateDefaultDesc.isPending ? 'Salvando...' : 'Salvar configuracoes'}
                </Button>
              </Card>
            )}

          </TabsContent>
        </Tabs>
      )}

      {/* Tradutor / Dublagem — sempre visivel, independente de canal */}
      <div className="space-y-6">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Languages className="h-5 w-5" />
          Tradutor / Dublagem
        </h2>

        {/* Warning if no API key */}
        {elevenLabsKey && !elevenLabsKey.hasKey && (
          <Card className="p-4 border-yellow-500/30 bg-yellow-500/5">
            <p className="text-sm text-yellow-500">
              Voce ainda nao configurou sua API Key do ElevenLabs. Configure abaixo para comecar.
            </p>
          </Card>
        )}

        {/* New dubbing form */}
        <Card className="p-6 space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Languages className="h-4 w-4" />
            Nova Traducao / Dublagem
          </h3>

          {/* Video selector grid */}
          {(() => {
            const hasYt = !!(videos && videos.length > 0)
            const hasR2 = r2Videos.length > 0
            const activeFilter = dubbingSource ?? (hasYt ? 'yt' : hasR2 ? 'r2' : null)
            const gridVideos = activeFilter === 'yt'
              ? (videos ?? []).map(v => ({ id: v.videoId, type: 'yt' as const, title: v.title, thumb: v.thumbnailUrl, duration: v.duration, url: `https://www.youtube.com/watch?v=${v.videoId}` }))
              : activeFilter === 'r2'
              ? r2Videos.map(v => ({ id: v.id, type: 'r2' as const, title: v.filename, thumb: null, duration: null, url: v.url }))
              : []

            return (hasYt || hasR2) ? (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Selecionar video existente</h4>
                <div className="flex gap-1">
                  {hasYt && (
                    <Button
                      size="sm"
                      variant={activeFilter === 'yt' ? 'default' : 'outline'}
                      className="h-7 text-xs"
                      onClick={() => setDubbingSource('yt')}
                    >
                      <Youtube className="mr-1 h-3 w-3" />
                      YouTube
                    </Button>
                  )}
                  {hasR2 && (
                    <Button
                      size="sm"
                      variant={activeFilter === 'r2' ? 'default' : 'outline'}
                      className="h-7 text-xs"
                      onClick={() => setDubbingSource('r2')}
                    >
                      <Film className="mr-1 h-3 w-3" />
                      Biblioteca
                    </Button>
                  )}
                </div>

                {gridVideos.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[280px] overflow-y-auto rounded-md border border-border p-2">
                    {gridVideos.map((v) => {
                      const isSelected = selectedGridVideoId === v.id
                      return (
                        <button
                          key={v.id}
                          type="button"
                          className={`relative rounded-md overflow-hidden border text-left transition-colors ${isSelected ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/50'}`}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedGridVideoId(null)
                              setDubbingUrl('')
                              setDubbingName('')
                            } else {
                              setSelectedGridVideoId(v.id)
                              setDubbingUrl(v.url)
                              setDubbingName(v.title)
                              setDubbingFile(null)
                            }
                          }}
                        >
                          <div className="aspect-video bg-muted flex items-center justify-center">
                            {v.thumb ? (
                              <img src={v.thumb} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <FileVideo className="h-6 w-6 text-muted-foreground" />
                            )}
                            {v.duration && (
                              <span className="absolute bottom-1 right-1 bg-black/75 text-white text-[10px] px-1 rounded">
                                {formatDuration(v.duration)}
                              </span>
                            )}
                            {isSelected && (
                              <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                <Check className="h-5 w-5 text-primary" />
                              </div>
                            )}
                          </div>
                          <p className="text-[11px] leading-tight p-1 truncate">{v.title}</p>
                        </button>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Nenhum video disponivel</p>
                )}

                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <div className="h-px flex-1 bg-border" />
                  <span>ou</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              </div>
            ) : null
          })()}

          {/* URL or File input */}
          <div className="space-y-3">
            <div>
              <Label>URL do video (YouTube ou outro)</Label>
              <div className="mt-1.5 flex gap-2">
                <div className="relative flex-1">
                  <Link className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={dubbingUrl}
                    onChange={(e) => { setDubbingUrl(e.target.value); setDubbingFile(null); setSelectedGridVideoId(null); setDubbingName('') }}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="pl-10"
                    disabled={!!dubbingFile}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border" />
              <span>ou</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div>
              <Label>Upload de arquivo</Label>
              <div className="mt-1.5">
                {dubbingFile ? (
                  <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                    <FileVideo className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate flex-1">{dubbingFile.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {(dubbingFile.size / 1024 / 1024).toFixed(1)} MB
                    </span>
                    <button onClick={() => setDubbingFile(null)} className="text-muted-foreground hover:text-foreground">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border px-3 py-3 text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors">
                    <Upload className="h-4 w-4" />
                    Clique para selecionar video ou audio
                    <input
                      type="file"
                      className="hidden"
                      accept="video/*,audio/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0]
                        if (f) { setDubbingFile(f); setDubbingUrl(''); setSelectedGridVideoId(null); setDubbingName('') }
                        e.target.value = ''
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Language selectors */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Idioma de origem</Label>
              <Select value={dubbingSourceLang} onValueChange={setDubbingSourceLang}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detectar</SelectItem>
                  <SelectItem value="pt">Portugues</SelectItem>
                  <SelectItem value="en">Ingles</SelectItem>
                  <SelectItem value="es">Espanhol</SelectItem>
                  <SelectItem value="fr">Frances</SelectItem>
                  <SelectItem value="de">Alemao</SelectItem>
                  <SelectItem value="it">Italiano</SelectItem>
                  <SelectItem value="ja">Japones</SelectItem>
                  <SelectItem value="ko">Coreano</SelectItem>
                  <SelectItem value="zh">Chines</SelectItem>
                  <SelectItem value="ru">Russo</SelectItem>
                  <SelectItem value="ar">Arabe</SelectItem>
                  <SelectItem value="hi">Hindi</SelectItem>
                  <SelectItem value="tr">Turco</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Idioma de destino</Label>
              <Select value={dubbingTargetLang} onValueChange={setDubbingTargetLang}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">Ingles</SelectItem>
                  <SelectItem value="es">Espanhol</SelectItem>
                  <SelectItem value="pt">Portugues</SelectItem>
                  <SelectItem value="fr">Frances</SelectItem>
                  <SelectItem value="de">Alemao</SelectItem>
                  <SelectItem value="it">Italiano</SelectItem>
                  <SelectItem value="ja">Japones</SelectItem>
                  <SelectItem value="ko">Coreano</SelectItem>
                  <SelectItem value="zh">Chines</SelectItem>
                  <SelectItem value="ru">Russo</SelectItem>
                  <SelectItem value="ar">Arabe</SelectItem>
                  <SelectItem value="hi">Hindi</SelectItem>
                  <SelectItem value="tr">Turco</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            onClick={handleCreateDubbing}
            disabled={createDubbing.isPending || (!dubbingUrl && !dubbingFile)}
          >
            {createDubbing.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Languages className="mr-2 h-4 w-4" />
            )}
            {createDubbing.isPending ? 'Enviando...' : 'Traduzir'}
          </Button>
        </Card>

        {/* Job history */}
        <div className="space-y-3">
          <h3 className="font-semibold text-sm">Historico de Dublagens</h3>

          {dubbingLoading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
            </div>
          ) : !dubbingJobs || dubbingJobs.length === 0 ? (
            <Card className="p-6 text-center text-muted-foreground">
              <Languages className="mx-auto h-10 w-10 opacity-30 mb-2" />
              <p>Nenhuma dublagem realizada</p>
              <p className="text-xs mt-1">Cole uma URL ou envie um video acima para comecar</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {dubbingJobs.map((job) => (
                <Card key={job.dubbing_id} className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{job.name || job.dubbing_id}</p>
                        {job.status === 'dubbing' && (
                          <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 shrink-0">
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            Processando
                          </Badge>
                        )}
                        {job.status === 'dubbed' && (
                          <Badge variant="outline" className="bg-green-500/10 text-green-500 shrink-0">
                            Concluido
                          </Badge>
                        )}
                        {job.status === 'error' && (
                          <Badge variant="outline" className="bg-red-500/10 text-red-500 shrink-0">
                            Erro
                          </Badge>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{job.source_lang || '?'} → {job.target_lang}</span>
                        {job.expected_duration_sec && (
                          <span>~{Math.ceil(job.expected_duration_sec / 60)} min</span>
                        )}
                        {job.error && (
                          <span className="text-red-400">{job.error}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {job.status === 'dubbed' && (
                        <Button
                          size="sm"
                          className="h-8"
                          onClick={() => {
                            const fname = `${job.name || 'dubbed'}_${job.target_lang}.mp4`
                            downloadDubbedVideo(job.dubbing_id, job.target_lang, fname)
                              .catch((err) => toast.error(err.message))
                          }}
                        >
                          <Download className="mr-1.5 h-3.5 w-3.5" />
                          Download
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDeleteDubbing(job)}
                        disabled={deleteDubbing.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ElevenLabs API Key — sempre visivel */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="font-semibold mb-1 flex items-center gap-2">
            <Languages className="h-4 w-4" />
            ElevenLabs - Traducao / Dublagem
          </h3>
          <p className="text-xs text-muted-foreground">
            Para usar a funcionalidade de dublagem automatica, cadastre sua API Key do ElevenLabs.
            Crie uma conta em{' '}
            <a href="https://elevenlabs.io" target="_blank" rel="noopener noreferrer" className="underline text-primary">
              elevenlabs.io
            </a>{' '}
            e copie sua key em Profile {'>'} API Keys.
          </p>
        </div>

        {elevenLabsKey?.hasKey && !showKeyInput ? (
          <div className="flex items-center gap-3">
            <div className="flex-1 flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
              <span className="text-muted-foreground">Key:</span>
              <code className="text-xs">{elevenLabsKey.maskedKey}</code>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowKeyInput(true)}
            >
              Alterar
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                if (!confirm('Remover sua API Key do ElevenLabs?')) return
                deleteElevenLabsKey.mutate(undefined, {
                  onSuccess: () => toast.success('API Key removida'),
                  onError: (err) => toast.error(err.message),
                })
              }}
              disabled={deleteElevenLabsKey.isPending}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              type="password"
              value={elevenLabsKeyInput}
              onChange={(e) => setElevenLabsKeyInput(e.target.value)}
              placeholder="sk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className="flex-1 font-mono text-sm"
            />
            <Button
              onClick={() => {
                if (!elevenLabsKeyInput.trim()) return
                saveElevenLabsKey.mutate(elevenLabsKeyInput.trim(), {
                  onSuccess: () => {
                    toast.success('API Key salva com sucesso')
                    setElevenLabsKeyInput('')
                    setShowKeyInput(false)
                  },
                  onError: (err) => toast.error(err.message),
                })
              }}
              disabled={saveElevenLabsKey.isPending || !elevenLabsKeyInput.trim()}
            >
              {saveElevenLabsKey.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Salvar Key
            </Button>
            {showKeyInput && (
              <Button variant="ghost" onClick={() => { setShowKeyInput(false); setElevenLabsKeyInput('') }}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Dialogs */}
      {activeChannelId && (
        <>
          <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} channelId={activeChannelId} />
          <EditVideoDialog
            open={!!editVideo}
            onOpenChange={(v) => !v && setEditVideo(null)}
            video={editVideo}
            channelId={activeChannelId}
          />
        </>
      )}
      <TagPresetEditor open={tagPresetsOpen} onOpenChange={setTagPresetsOpen} />
    </div>
  )
}
